/**
 * A client's portal access — the client page's Portal access tab.
 *
 * A client has one login: its own email and mobile number, kept apart from the
 * contact details on the overview, and it signs in with a password or a
 * one-time code sent to either. This is where the portal is turned on, the login
 * set, a password issued and a lockout cleared.
 *
 * It is also where the portal's features are chosen for the client — Support,
 * Invoices. The server narrows the client's session to the features switched on
 * here, so a feature left off is unreachable from the portal, not merely
 * untabbed. A feature this installation does not have at all is not offered as
 * a switch: it is named in a note, because switching it on could do nothing.
 *
 * Editing is offered only to whoever may edit portal access; everybody else sees
 * the state read-only. Absent rather than disabled, as everywhere.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, LockOpen } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Input,
  QueryPending,
  ReadOnly,
  Switch,
  api,
  formatDateTime,
  toast,
  useCan,
} from '@connect/web-shared';

import { ResetPasswordDialog } from '../access/reset-password-dialog';

/** One portal feature, and whether this installation has its service. */
export interface PortalServiceOption {
  id: 'support' | 'finance' | string;
  label: string;
  available: boolean;
}

export interface PortalAccess {
  client_public_id: string;
  portal_enabled: boolean;
  login_email: string | null;
  login_mobile: string | null;
  has_password: boolean;
  must_change_password: boolean;
  locked_until: string | null;
  last_login_at: string | null;
  sms_codes_available: boolean;
  /** Codes to a mobile go by WhatsApp first where Connect's own WhatsApp is set up. */
  whatsapp_codes_available: boolean;
  email_codes_available: boolean;
  /** The portal features switched on for the client. */
  portal_services: string[];
  portal_service_options: PortalServiceOption[];
}

/** What each feature gives the client, for the switch's hint. */
const FEATURE_HINTS: Record<string, string> = {
  support: 'Raise requests and follow the replies.',
  finance: 'See invoices and what is owed.',
};

export function portalAccessQueryKey(clientPublicId: string) {
  return ['crm', 'clients', clientPublicId, 'portal-access'] as const;
}

function sameSet(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((value) => right.includes(value));
}

export function PortalAccessPanel({
  clientPublicId,
  clientName,
}: {
  clientPublicId: string;
  clientName: string;
}) {
  const queryClient = useQueryClient();
  const canEdit = useCan('crm.portal_access.edit');
  const endpoint = `/crm/clients/${clientPublicId}/portal-access`;
  const [enabled, setEnabled] = useState(false);
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [features, setFeatures] = useState<string[]>([]);
  const [resetting, setResetting] = useState(false);

  const access = useQuery({
    queryKey: portalAccessQueryKey(clientPublicId),
    queryFn: () => api.get<PortalAccess>(endpoint),
  });

  useEffect(() => {
    if (!access.data) return;
    setEnabled(access.data.portal_enabled);
    setEmail(access.data.login_email ?? '');
    setMobile(access.data.login_mobile ?? '');
    setFeatures(access.data.portal_services ?? []);
  }, [access.data]);

  const saved = (data: PortalAccess) => {
    queryClient.setQueryData(portalAccessQueryKey(clientPublicId), data);
    // The overview shows whether the portal is on.
    void queryClient.invalidateQueries({ queryKey: ['crm', 'clients', clientPublicId] });
  };

  const save = useMutation({
    mutationFn: () =>
      api.patch<PortalAccess>(endpoint, {
        portal_enabled: enabled,
        login_email: email.trim() || null,
        login_mobile: mobile.trim() || null,
        portal_services: features,
      }),
    onSuccess: (data) => {
      saved(data);
      toast.success('Portal access saved');
    },
  });

  const unlock = useMutation({
    mutationFn: () => api.post<PortalAccess>(`${endpoint}/unlock`),
    onSuccess: (data) => {
      saved(data);
      toast.success('Unlocked', `${clientName} can sign in again.`);
    },
    onError: (error) =>
      toast.error('Could not unlock', error instanceof ApiError ? error.message : undefined),
  });

  const row = access.data;
  const fieldErrors = save.error instanceof ApiError ? save.error.fieldErrors : {};
  const options = row?.portal_service_options ?? [];
  const offered = options.filter((option) => option.available);
  const unavailable = options.filter((option) => !option.available);
  const changed =
    row !== undefined &&
    (enabled !== row.portal_enabled ||
      email.trim() !== (row.login_email ?? '') ||
      mobile.trim() !== (row.login_mobile ?? '') ||
      !sameSet(features, row.portal_services ?? []));

  return (
    <ReadOnly when={!canEdit}>
      <Card>
        <CardHeader
          title="Portal access"
          actions={
            canEdit && row?.locked_until ? (
              <Button
                variant="secondary"
                size="sm"
                loading={unlock.isPending}
                onClick={() => unlock.mutate()}
              >
                <LockOpen className="size-3.5" />
                Unlock
              </Button>
            ) : null
          }
        />
        <CardBody>
          {row === undefined ? (
            <QueryPending query={access} label="Loading portal access" />
          ) : (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
                {row.portal_enabled ? (
                  <Badge tone="success">Portal on</Badge>
                ) : (
                  <Badge tone="neutral">Portal off</Badge>
                )}
                {!row.has_password ? (
                  <Badge tone="neutral">No password — signs in with codes</Badge>
                ) : row.must_change_password ? (
                  <Badge tone="warning">
                    <KeyRound className="size-2.5" />
                    Must change password
                  </Badge>
                ) : (
                  <Badge tone="info">Password set</Badge>
                )}
                {row.locked_until ? (
                  <Badge tone="danger">Locked until {formatDateTime(row.locked_until)}</Badge>
                ) : null}
                <span className="ml-1">
                  {row.last_login_at
                    ? `Last signed in ${formatDateTime(row.last_login_at)}`
                    : 'Never signed in'}
                </span>
              </div>
  
              {save.error ? (
                <Alert tone="danger" title="Could not save">
                  {save.error instanceof ApiError
                    ? save.error.message
                    : 'Could not reach the server. Try again.'}
                </Alert>
              ) : null}
  
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="Login email"
                  type="email"
                  value={email}
                  autoComplete="off"
                  error={fieldErrors.login_email}
                  hint="Signs in with a password or an emailed code. Not the contact email."
                  onChange={(event) => setEmail(event.target.value)}
                />
                <Input
                  label="Login mobile"
                  inputMode="tel"
                  value={mobile}
                  autoComplete="off"
                  placeholder="98480 22338"
                  error={fieldErrors.login_mobile}
                  hint="A 10-digit Indian mobile. Signs in with a password or a code sent by WhatsApp or SMS."
                  onChange={(event) => setMobile(event.target.value)}
                />
              </div>
  
              <Switch
                label="Portal on"
                checked={enabled}
                hint={`Off, ${clientName} cannot sign in, and a session already open ends on its next request.`}
                onChange={(event) => setEnabled(event.target.checked)}
              />
  
              {options.length > 0 ? (
                <div className="space-y-3 border-t border-line pt-4">
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium text-heading">Portal features</p>
                    <p className="text-xs text-muted">
                      What {clientName} can use in the portal. Messaging appears on its own for each
                      messaging account connected to the client.
                    </p>
                  </div>
                  {fieldErrors.portal_services ? (
                    <p className="text-xs text-danger" role="alert">
                      {fieldErrors.portal_services}
                    </p>
                  ) : null}
                  {offered.map((option) => (
                    <Switch
                      key={option.id}
                      label={option.label}
                      hint={FEATURE_HINTS[option.id]}
                      checked={features.includes(option.id)}
                      onChange={(event) =>
                        setFeatures((current) =>
                          event.target.checked
                            ? [...current.filter((id) => id !== option.id), option.id]
                            : current.filter((id) => id !== option.id),
                        )
                      }
                    />
                  ))}
                  {unavailable.length > 0 ? (
                    // Why the switch is not here, and where it is switched on. A
                    // portal feature is a service of this installation first: it
                    // cannot be given to one client while the installation does
                    // not have it at all.
                    <p className="text-xs text-muted">
                      {unavailable.map((option) => option.label).join(' and ')}{' '}
                      {unavailable.length === 1 ? 'is' : 'are'} not enabled for this installation.{' '}
                      <Can permission="core.service.view">
                        <Link to="/settings/services" className="text-accent hover:underline">
                          Enable {unavailable.length === 1 ? 'it' : 'them'} under Services
                        </Link>{' '}
                        first, and {unavailable.length === 1 ? 'it appears' : 'they appear'} here.
                      </Can>
                    </p>
                  ) : null}
                </div>
              ) : null}
  
              {row.login_mobile && !row.sms_codes_available && !row.whatsapp_codes_available ? (
                <Alert tone="warning">
                  Sign-in codes by WhatsApp or SMS are not configured on this installation, so{' '}
                  {clientName} needs a password to sign in by mobile until they are.
                </Alert>
              ) : row.login_mobile && row.whatsapp_codes_available ? (
                <p className="text-xs text-muted">
                  A code to {clientName}'s mobile goes by WhatsApp
                  {row.sms_codes_available ? ', and by SMS if WhatsApp cannot deliver it' : ''}.
                </p>
              ) : null}
              {row.login_email && !row.email_codes_available ? (
                <Alert tone="info">
                  Emailed sign-in codes are not available yet, so {clientName} signs in by email
                  with a password.
                </Alert>
              ) : null}
  
              {canEdit ? (
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line pt-4">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!row.login_email && !row.login_mobile}
                    onClick={() => setResetting(true)}
                  >
                    <KeyRound className="size-3.5" />
                    {row.has_password ? 'Reset password' : 'Set a password'}
                  </Button>
                  <Button
                    size="sm"
                    loading={save.isPending}
                    disabled={!changed}
                    onClick={() => save.mutate()}
                  >
                    Save
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </CardBody>
  
        <ResetPasswordDialog
          open={resetting}
          onClose={() => setResetting(false)}
          endpoint={`${endpoint}/reset-password`}
          name={clientName}
        />
      </Card>
    </ReadOnly>
  );
}
